import { useInsiderMembershipInformation } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderMembershipInformation";
import { intlDate } from "@faire/web--source/common/localization/date-fns/intlDate";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import { IMembership } from "@faire/web-api--source/indigofair/data/IMembership";
import { IInsiderMembershipInformation } from "@faire/web-api--source/indigofair/insider/IInsiderMembershipInformation";
import { route as brandPage } from "@faire/web-api--source/routes/brand/brandToken";
import shoppingBag from "@faire/web-api--source/routes/cart";
import checkout from "@faire/web-api--source/routes/checkout";
import { route as productDescriptionPage } from "@faire/web-api--source/routes/product/productToken";
import insiderMembershipInfo from "@faire/web-api--source/routes/user/insider/info";

export interface IUseInsiderStateResult {
  /** The current state of the Insider Membership. Can be one of the values defined in `IInsiderMembershipInformation.State`. */
  insiderState: keyof typeof IInsiderMembershipInformation.State | undefined;

  /** Boolean indicating whether the Insider Membership is in any of the following states: ELIGIBLE, TRIALING, ACTIVE, UNPAID, CANCELLED, PAUSED. */
  isInsider: boolean;

  /** Boolean indicating whether the Insider Membership is in the ELIGIBLE or CANCELLED state. */
  isInsiderEligible: boolean;

  /** Boolean indicating whether the Insider Membership is in the TRIALING state. */
  isInsiderTrial: boolean;

  /** Boolean indicating whether the Insider Membership is in the ACTIVE, TRIALING, or AUTO_ENROLLED_TRIAL (XP - AUTO_ENROLL_INTO_INSIDER_ON_SIGN_UP_240311) state. */
  isInsiderActive: boolean;

  /** Boolean indicating whether the Insider Membership is in the UNPAID state. */
  isInsiderUnpaid: boolean;

  /** Boolean indicating whether the Insider Membership is in the CANCELLED state. */
  isInsiderCancelled: boolean;

  /** Boolean indicating whether the Insider Membership is in the PAUSED state. */
  isInsiderPaused: boolean;

  /** Boolean indicating whether the Insider Membership is in the PAUSED state or will be in PAUSED state in the next period. */
  isInsiderPausedOrWillPause: boolean;

  /** Boolean indicating whether the Insider Membership will be in PAUSED state in the next period. */
  isInsiderUpcomingPaused: boolean;

  /** Boolean indicating whether the Insider Membership is in the AUTO_ENROLLED_TRIAL state. */
  isInsiderAutoEnrollTrial: boolean;

  /** String indicating whether the Insider Membership is in the UNPAID, CANCELLED, or TRIAL state. It will have the values "unpaid", "churned" or "trial" respectively. */
  insiderMembershipStateEventPrefix: string;

  /** String indicating where the source of the insider modal page is */
  insiderModalSourcePageEventPrefix: IMembership.Channel;

  /** String indicating when insider was active */
  insiderSinceYear: string;
}

/**
 * A hook that provides state-related information for Insider Membership.
 *
 * This hook returns an object that provides the current state of the Insider Membership, as well as booleans indicating whether the membership is in a particular state.
 * It relies on the `useInsiderMembershipInformation` hook to fetch the relevant information.
 *
 * @returns {IUseInsiderStateResult} An object containing Insider Membership state-related information.
 */
export function useInsiderState(): IUseInsiderStateResult {
  return getInsiderState(
    useInsiderMembershipInformation().insiderMembershipInformation
  );
}

/**
 * A function that provides state-related information for Insider Membership.
 *
 * To call this using a hook clients should call `useInsiderState()`.  This function can also be called with the
 * `insiderMembershipInformation` provided to allow server-side fetching of the data and passing in the server-fetched data.
 *
 * @returns {IUseInsiderStateResult} An object containing Insider Membership state-related information.
 */
export function getInsiderState(
  insiderMembershipInformation: IInsiderMembershipInformation | undefined,
  pathname?: string
): IUseInsiderStateResult {
  const insiderState =
    insiderMembershipInformation?.state ??
    IInsiderMembershipInformation.State.STATE_UNKNOWN;

  const isInsiderEligible =
    insiderState === IInsiderMembershipInformation.State.ELIGIBLE ||
    insiderState === IInsiderMembershipInformation.State.CANCELLED;

  const isInsiderTrial =
    insiderState === IInsiderMembershipInformation.State.TRIALING;

  const isInsiderUnpaid =
    insiderState === IInsiderMembershipInformation.State.UNPAID;

  const isInsiderAutoEnrollTrial =
    insiderState === IInsiderMembershipInformation.State.AUTO_ENROLLED_TRIAL;

  const isInsiderActive =
    insiderState === IInsiderMembershipInformation.State.ACTIVE ||
    isInsiderTrial ||
    isInsiderUnpaid ||
    isInsiderAutoEnrollTrial;

  const isInsiderCancelled =
    insiderState === IInsiderMembershipInformation.State.CANCELLED;

  const isInsiderPaused =
    insiderState === IInsiderMembershipInformation.State.PAUSED;

  const isInsider =
    isInsiderEligible ||
    isInsiderActive ||
    isInsiderUnpaid ||
    isInsiderCancelled ||
    isInsiderPaused ||
    isInsiderAutoEnrollTrial;

  const isInsiderWillPause =
    insiderMembershipInformation?.action_at_period_end ===
      IMembership.ActionAtPeriodEnd.PAUSE &&
    !!insiderMembershipInformation?.paused_until;

  const isInsiderUpcomingPaused = isInsiderWillPause && !isInsiderPaused;

  const isInsiderPausedOrWillPause = isInsiderPaused || isInsiderWillPause;

  const insiderSinceYear = intlDate(
    insiderMembershipInformation?.membership_created_at ?? 0,
    "yyyy"
  );

  const insiderMembershipStateEventPrefix =
    getInsiderMembershipStateEventPrefix(insiderState);

  const insiderModalSourcePageEventPrefix: IMembership.Channel =
    getInsiderModalSourcePageEventPrefix(pathname);

  return {
    isInsiderUpcomingPaused,
    insiderState,
    isInsider,
    isInsiderEligible,
    isInsiderTrial,
    isInsiderActive,
    isInsiderUnpaid,
    isInsiderCancelled,
    isInsiderPaused,
    isInsiderAutoEnrollTrial,
    isInsiderPausedOrWillPause,
    insiderMembershipStateEventPrefix,
    insiderModalSourcePageEventPrefix,
    insiderSinceYear,
  };
}

const getInsiderMembershipStateEventPrefix = (
  state: keyof typeof IInsiderMembershipInformation.State
) => {
  switch (state) {
    case IInsiderMembershipInformation.State.UNPAID:
      return "unpaid";

    case IInsiderMembershipInformation.State.CANCELLED:
      return "churned";

    default:
      return "trial";
  }
};

const getInsiderModalSourcePageEventPrefix = (
  pathname?: string
): IMembership.Channel => {
  const currentPath = pathname ?? getCurrentPath().pathname;
  if (currentPath) {
    switch (removeLocaleFromUrl(currentPath)) {
      case insiderMembershipInfo():
        return IMembership.Channel.MARKETING_PAGE;
      case shoppingBag():
        return IMembership.Channel.SHOPPING_BAG;
      case brandPage:
        return IMembership.Channel.BRAND_PAGE;
      case productDescriptionPage:
        return IMembership.Channel.PDP;
      case checkout():
        return IMembership.Channel.CHECKOUT;
      default:
        return IMembership.Channel.CHANNEL_UNKNOWN;
    }
  }
  return IMembership.Channel.CHANNEL_UNKNOWN;
};
